//PaymentTypeSummary
import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Container,
  Badge,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

import DaybookService from "../../services/OrderingServices/DaybookService";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SettingService from "../../services/OrderingServices/SettingService";
import ConfirmationDaybook from "../../OrderingSystem/Dashboard/components/CardComponent/ConfirmationDaybook";
import CloseSummaryPrint from "../../OrderingSystem/Dashboard/components/CardComponent/CloseSummaryPrint";
import NumberWithCommas from "../../pages/Pages/Starter/NumberWithCommas";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const SummaryCard = ({ title, value, color, icon }) => (
  <Card className="shadow-sm card-animate border-0">
    <CardBody className="d-flex align-items-center justify-content-between">
      <div className="w-100">
        <h6 className="fw-semibold text-uppercase" style={{ color }}>
          {title}
        </h6>
        <div className="d-flex justify-content-between w-100">
          <h4 className="fs-2 fw-bold" style={{ color }}>
            <NumberWithCommas number={value} />
          </h4>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: color + "20",
              color,
              width: "36px",
              height: "36px",
            }}
          >
            <i className={`${icon} fs-5`} />
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

const PaymentTypeSummary = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isDayOpen, setIsDayOpen] = useState(null);
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [responseModal, setResponseModal] = useState(false);
  const [responseData, setResponseData] = useState([]);

  // Summary statistics
  const [ongoingOrderCount, setOngoingOrderCount] = useState(0);
  const [ongoingOrderAmount, setOngoingOrderAmount] = useState(0);
  const [totalSalesAmount, setTotalSalesAmount] = useState("");
  const [expectedSalesAmount, setExpectedSalesAmount] = useState(0);

  const toggleResponse = () => setResponseModal(!responseModal);
  const toggleConfirm = () => setShowCloseModal(!showCloseModal);

  // Get current date display
  const getDateRangeDisplay = () => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const end = new Date(endDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  // Calculate summary stats
  const calculateStats = (data) => {
    if (!data || data.length === 0) {
      setTotalPaymentTypes(0);
      setTotalAmount(0);
      setHighestPaymentType("");
      setHighestAmount(0);
      return;
    }

    const total = data.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
    const highest = data.reduce(
      (max, item) =>
        item.totalAmount > max.amount
          ? { name: item.paymentTypeName, amount: item.totalAmount }
          : max,
      { name: "", amount: 0 }
    );

    setTotalPaymentTypes(data.length);
    setTotalAmount(total);
    setHighestPaymentType(highest.name);
    setHighestAmount(highest.amount);
  };

  // Initialize date range
  useEffect(() => {
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 1);
    setStartDate(oneMonthBefore);
    setEndDate(today);
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [paymentResult, orderResult, settingResult] =
          await Promise.allSettled([
            DaybookService.paymentTypeSummary(),
            DaybookService.orderSummary(),
            SettingService.getDaySetting(),
          ]);

        if (paymentResult.status === "fulfilled") {
          const paymentData = paymentResult.value.paymentSummary || [];

          const result = paymentResult.value;
          setSummary(result.paymentSummary || []);
          // calculateStats(paymentData);

          setOngoingOrderAmount(result?.ongoingOrderAmount);
          setOngoingOrderCount(result?.ongoingOrderCount);
          setTotalSalesAmount(result?.totalSalesAmount);
          setExpectedSalesAmount(result?.expectedSalesAmount);
        } else {
          console.error(
            "Error fetching payment summary:",
            paymentResult.reason
          );
          toast.error(
            paymentResult.reason?.response?.data?.message ||
              "Payment summary failed"
          );
        }

        if (orderResult.status === "fulfilled") {
          setOrderData(orderResult.value || []);
        }

        if (settingResult.status === "fulfilled") {
          setIsDayOpen(settingResult.value.isActive);
          setIsDayClosed(!settingResult.value.isActive);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle day toggle
  const handleDayToggle = async () => {
    const newStatus = !isDayOpen;
    if (!newStatus) {
      setShowCloseModal(true);
      return;
    }
    await performDayToggle(newStatus);
  };

  // Perform day toggle
  const performDayToggle = async (newStatus) => {
    setIsLoading(true);
    try {
      if (!newStatus) {
        const response = await DaybookService.closeSummary();
        setResponseData(response);
        setIsDayOpen(false);
        setIsDayClosed(true);
        toggleResponse();
        toast.success("Day is closed!");
      } else {
        const response = await SettingService.setOpenDay();
        setIsDayOpen(true);
        setIsDayClosed(false);
        toast.success(response);
      }
    } catch (error) {
      console.error("Error updating day setting:", error);
      toast.error(error?.response?.data || "Failed to toggle day status");
      setIsDayOpen(!newStatus);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle print
  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  // DataTable columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Payment Type Name",
      selector: (row) => row.paymentTypeName,
      sortable: true,
    },
    {
      name: "Total Amount",
      cell: (row) => <NumberWithCommas number={row.totalAmount} />,
      right: true,
    },
  ];
  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  // Get current date
  const getCurrentDate = () => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Payment Type Summary"
          pageTitle="Dashboard"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Day Status Card */}
        <Card className="shadow-sm border-0 mb-3">
          <CardBody className="py-3">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: isDayOpen ? "#d4edda" : "#f8d7da",
                      width: "48px",
                      height: "48px",
                    }}
                  >
                    <i
                      className={`bx ${isDayOpen ? "bx-sun" : "bx-moon"} fs-4`}
                      style={{ color: isDayOpen ? "#28a745" : "#dc3545" }}
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0 fw-bold">
                        {isDayOpen ? "Day is Currently Open" : "Day is Closed"}
                      </h6>
                      <Badge
                        color={isDayOpen ? "success" : "danger"}
                        className="fs-6"
                        style={{ fontWeight: "500" }}
                      >
                        {isDayOpen ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-muted mb-0 small mt-1">
                      <i className="bx bx-calendar me-1"></i>
                      {getCurrentDate()}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={6} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex justify-content-md-end align-items-center gap-3">
                  <span className="text-muted small">Toggle Day Status</span>
                  <div className="form-check form-switch form-switch-lg mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isDayOpen}
                      onChange={handleDayToggle}
                      disabled={isLoading}
                      style={{
                        cursor: "pointer",
                        width: "3rem",
                        height: "1.5rem",
                      }}
                    />
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handlePrint}
                    className="d-flex align-items-center gap-2"
                    disabled={isPrinting}
                  >
                    <i className="ri-printer-line"></i> Print
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        <Row className="mt-2">
          {" "}
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Ongoing Order Count"
              value={ongoingOrderCount}
              color="#17a2b8"
              icon="bx bx-trending-up"
            />
          </Col>{" "}
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Ongoing Order Amount"
              value={ongoingOrderAmount}
              color="#28a745"
              icon="bx bx-dollar-circle"
            />
          </Col>{" "}
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Expected Sales Amount"
              value={expectedSalesAmount}
              color="#ffc107"
              icon="bx bx-money"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Actual Sales Amount"
              value={totalSalesAmount}
              color="#0d6efd"
              icon="bx bx-credit-card"
            />
          </Col>
        </Row>

        {/* Payment Type List */}
        <div
          className="p-3 p-lg-4"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1 fw-bold text-dark">Payment Type Summary</h5>
              <p className="text-muted small mb-0">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>

          </div>
          <hr style={{ borderColor: "#dee2e6", marginTop: "0.5rem" }} />

          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1 ms-3">Loading...</h5>
            </div>
          ) : (
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              highlightOnHover
              pointerOnHover
              persistTableHead
              progressPending={isLoading}
              customStyles={customStyles}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={summary}
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">
                    No Payment Data Found
                  </h5>
                  <p className="text-muted mb-0">
                    No payment records available for the selected date range.
                  </p>
                </div>
              }
            />
          )}
        </div>

        {/* Modals */}
        <ConfirmationDaybook
          isOpen={showCloseModal}
          toggle={toggleConfirm}
          orderData={orderData}
          performDayToggle={performDayToggle}
        />

        <CloseSummaryPrint
          isOpen={responseModal}
          toggle={toggleResponse}
          data={responseData}
        />
      </div>
    </Container>
  );
};

export default PaymentTypeSummary;
